/* Compatible with Shopify's modal-opener and product-info; single-slide accessible lightbox. */
if (!customElements.get('product-modal')) {
  customElements.define('product-modal', class ProductModal extends HTMLElement {
    connectedCallback() {
      if (!this.moved) {
        this.moved = true;
        this.dataset.section = this.closest('.shopify-section')?.id.replace('shopify-section-', '') || '';
        document.body.appendChild(this);
        return;
      }
      this.dialog = this.querySelector('[role="dialog"]');
      this.content = this.querySelector('.product-media-modal__content');
      this.stage = this.querySelector('[data-si-gallery-stage]');
      this.onClick = event => {
        const target = event.target.closest('button');
        if (event.target === this || target?.hasAttribute('data-si-gallery-close')) return this.hide();
        if (target?.hasAttribute('data-si-gallery-step')) this.activate(this.index + Number(target.dataset.siGalleryStep));
        else if (target?.hasAttribute('data-si-gallery-target')) this.activate(this.slides.findIndex(s => s.dataset.mediaId === target.dataset.siGalleryTarget));
        else if (target?.hasAttribute('data-si-gallery-zoom')) this.toggleZoom();
        else if (event.target.matches('.si-lightbox__slide > img') && performance.now() > (this.suppressImageClickUntil || 0)) this.toggleZoom();
      };
      this.onKey = event => {
        if (event.key === 'Escape') { event.preventDefault(); this.hide(); return; }
        if (event.target.closest('input, textarea, video, model-viewer')) return;
        if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') { event.preventDefault(); this.activate(this.index + (event.key === 'ArrowRight' ? 1 : -1)); }
      };
      this.onTouchStart = event => {
        this.touch = !this.classList.contains('is-zoomed') && !event.target.closest('button,video,iframe,model-viewer') && event.touches.length === 1 ? { x: event.touches[0].clientX, y: event.touches[0].clientY } : null;
      };
      this.onTouchEnd = event => {
        if (!this.touch || !event.changedTouches.length) return;
        const dx = event.changedTouches[0].clientX - this.touch.x, dy = event.changedTouches[0].clientY - this.touch.y;
        if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy) * 1.4) this.activate(this.index + (dx < 0 ? 1 : -1));
        this.touch = null;
      };
      this.onPointerDown = event => {
        if (!this.classList.contains('is-zoomed') || event.pointerType === 'touch' || event.button !== 0 || !event.target.matches('.si-lightbox__slide > img')) return;
        event.preventDefault();
        this.drag = { id: event.pointerId, x: event.clientX, y: event.clientY, left: this.stage.scrollLeft, top: this.stage.scrollTop, moved: false };
        this.stage.setPointerCapture(event.pointerId);
      };
      this.onPointerMove = event => {
        if (!this.drag || event.pointerId !== this.drag.id) return;
        const dx = event.clientX - this.drag.x, dy = event.clientY - this.drag.y;
        if (Math.abs(dx) + Math.abs(dy) > 4) { this.drag.moved = true; this.classList.add('is-dragging'); }
        if (this.drag.moved) { this.stage.scrollLeft = this.drag.left - dx; this.stage.scrollTop = this.drag.top - dy; }
      };
      this.onPointerEnd = event => {
        if (!this.drag || event.pointerId !== this.drag.id) return;
        const clicked = !this.drag.moved && event.type === 'pointerup';
        this.suppressImageClickUntil = performance.now() + 350;
        this.endDrag();
        if (clicked) this.toggleZoom();
      };
      this.stage.addEventListener('pointerdown', this.onPointerDown);
      this.stage.addEventListener('pointermove', this.onPointerMove);
      this.stage.addEventListener('pointerup', this.onPointerEnd);
      this.stage.addEventListener('pointercancel', this.onPointerEnd);
      this.stage.addEventListener('lostpointercapture', this.onPointerEnd);
      this.addEventListener('click', this.onClick);
      this.addEventListener('keydown', this.onKey);
      this.stage.addEventListener('touchstart', this.onTouchStart, { passive: true });
      this.stage.addEventListener('touchend', this.onTouchEnd, { passive: true });
      this.observer = new MutationObserver(() => {
        const id = this.slides?.[this.index]?.dataset.mediaId;
        this.refresh();
        if (this.hasAttribute('open')) this.activate(Math.max(0, this.slides.findIndex(s => s.dataset.mediaId === id)));
      });
      this.observer.observe(this.content, { childList: true });
      this.refresh();
    }
    disconnectedCallback() {
      this.observer?.disconnect(); clearTimeout(this.preloadTimer); this.endDrag();
      this.removeEventListener('click', this.onClick); this.removeEventListener('keydown', this.onKey);
      this.stage?.removeEventListener('pointerdown', this.onPointerDown); this.stage?.removeEventListener('pointermove', this.onPointerMove);
      ['pointerup', 'pointercancel', 'lostpointercapture'].forEach(type => this.stage?.removeEventListener(type, this.onPointerEnd));
      this.stage?.removeEventListener('touchstart', this.onTouchStart); this.stage?.removeEventListener('touchend', this.onTouchEnd);
      if (this.hasAttribute('open')) this.hide();
    }
    refresh() {
      this.slides = [...this.content.querySelectorAll('[data-si-media-slide]')];
      this.querySelectorAll('[data-si-gallery-step],.si-lightbox__thumbs').forEach(el => { el.hidden = this.slides.length < 2; });
    }
    show(opener) {
      this.refresh(); if (!this.slides.length) return;
      this.openedBy = opener;
      this.wasLocked = document.body.classList.contains('overflow-hidden');
      document.body.classList.add('overflow-hidden');
      this.setAttribute('open', '');
      this.activate(Math.max(0, this.slides.findIndex(s => s.dataset.mediaId === opener?.dataset.mediaId)));
      if (typeof trapFocus === 'function') trapFocus(this.dialog, this.querySelector('[data-si-gallery-close]'));
      else this.querySelector('[data-si-gallery-close]').focus();
    }
    hide() {
      if (!this.hasAttribute('open')) return;
      this.removeAttribute('open'); clearTimeout(this.preloadTimer); this.resetZoom();
      if (!this.wasLocked) document.body.classList.remove('overflow-hidden');
      if (typeof pauseAllMedia === 'function') pauseAllMedia();
      if (typeof removeTrapFocus === 'function') removeTrapFocus(this.openedBy?.isConnected ? this.openedBy : null);
      document.body.dispatchEvent(new CustomEvent('modalClosed'));
    }
    activate(index) {
      if (!this.slides.length) return;
      if (typeof pauseAllMedia === 'function') pauseAllMedia();
      this.resetZoom();
      this.index = (index + this.slides.length) % this.slides.length;
      this.slides.forEach((slide, i) => { slide.hidden = i !== this.index; slide.classList.toggle('active', i === this.index); });
      const current = this.slides[this.index];
      const image = current.querySelector(':scope > img');
      if (image) { image.loading = 'eager'; image.draggable = false; }
      clearTimeout(this.preloadTimer);
      this.preloadTimer = setTimeout(() => {
        if (!this.hasAttribute('open')) return;
        const next = this.slides[(this.index + 1) % this.slides.length]?.querySelector(':scope > img');
        if (next) next.loading = 'eager';
      }, 160);
      this.querySelector('[data-si-gallery-count]').textContent = `${this.index + 1} / ${this.slides.length}`;
      this.querySelector('[data-si-gallery-zoom]').hidden = !current.querySelector(':scope > img');
      this.querySelectorAll('[data-si-gallery-target]').forEach(button => {
        const selected = button.dataset.siGalleryTarget === current.dataset.mediaId;
        if (selected) button.setAttribute('aria-current', 'true'); else button.removeAttribute('aria-current');
        if (selected && this.hasAttribute('open')) button.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      });
    }
    endDrag() {
      const drag = this.drag; this.drag = null; this.classList.remove('is-dragging');
      if (drag && this.stage.hasPointerCapture(drag.id)) this.stage.releasePointerCapture(drag.id);
    }
    resetZoom() {
      this.endDrag();
      this.classList.remove('is-zoomed');
      const button = this.querySelector('[data-si-gallery-zoom]');
      button.setAttribute('aria-pressed', 'false'); button.textContent = 'Powiększ';
      if (this.stage) { this.stage.scrollTop = 0; this.stage.scrollLeft = 0; }
    }
    toggleZoom() {
      if (!this.slides[this.index]?.querySelector(':scope > img')) return;
      const zoomed = this.classList.toggle('is-zoomed');
      const button = this.querySelector('[data-si-gallery-zoom]');
      button.setAttribute('aria-pressed', String(zoomed)); button.textContent = zoomed ? 'Dopasuj' : 'Powiększ';
    }
  });
}
